<template>
  <div class="mt-3">
    <div class="w-full flex justify-between mb-2">
      <div class="arrow arrow--left" :class="{ 'opacity-0': firstColumnIsVisible }" />
      <div class="arrow arrow--right" :class="{ 'opacity-0': lastColumnIsVisible }" />
    </div>
    <div class="w-full overflow-x-scroll">
      <table class="table min-w-full text-sm sm:text-base">
        <thead v-if="opts.header">
          <th class="py-3 px-2 align-top bg-black text-white" v-if="opts.rank"></th>
          <th
            v-for="(col, index) in columns"
            :key="col.key"
            class="py-3 px-2 align-top uppercase font-light bg-black text-white"
            :class="`column-type-${col.type}`"
            @click="sortTable(col.key)"
          >
            <span v-if="opts.sortable" class="inline-block w-3 text-brand-bright">
              <template v-if="sortKey === col.key && sortDirection">↓</template>
              <template v-if="sortKey === col.key && !sortDirection">↑</template>
            </span>
            {{ th(col) }}

            <span v-if="index === 0" id="firstColumn" v-observe-visibility="tableScroll" />
            <span v-if="index == columns.length - 1" id="lastColumn" v-observe-visibility="tableScroll" />
          </th>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in rows" :key="rowIndex">
            <td class="py-2 sm:py-3 px-2 align-top" v-if="opts.rank">{{ rowIndex + 1 }}</td>
            <td
              class="py-2 sm:py-3 px-2 align-top border-b border-brand-grey-light leading-5 tracking-tight"
              :class="`cell-type-${col.type}`"
              v-for="(col, colIndex) in columns"
              :key="col.key"
            >
              <span :class="col.options.class">
                <template v-if="colIndex === 0">
                  <router-link :to="url(row[col.key])">{{ iso3ToName(row[col.key]) }}</router-link>
                </template>
                <template v-else>
                  {{ cell(row[col.key], colIndex, row, col.options.formatter) }}
                </template>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="downloadLink" class="text-right pt-2">
      <a class="link uppercase text-sm" :href="downloadLink" download>{{ downloadLinkLabel }}</a>
    </div>
  </div>
</template>

<script>
import { iso3ToSlug, iso3ToName } from '@/utils/countryMap'
/**
 * Credits: The table in this commponent is very much inspired by https://observablehq.com/@tmcw/tables
 */
export default {
  name: 'Table',
  props: {
    tableData: {
      type: Array,
      required: true
    },
    downloadLink: {
      type: String,
      default: ''
    },
    options: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      defaultOptions: {
        header: true,
        columns: {},
        sortable: false
      },
      sortKey: undefined,
      sortDirection: true,
      firstColumnIsVisible: true,
      lastColumnIsVisible: false
    }
  },
  computed: {
    opts() {
      return Object.assign({}, this.defaultOptions, this.options)
    },
    dimension() {
      return this.$store.state.dimensionSlug
    },
    region() {
      return this.$store.state.regionSlug
    },
    downloadLinkLabel() {
      return this.$store.getters.localizedString('downloadLinkLabel')
    },
    sortedData() {
      if (this.sortKey) {
        const data = this.tableData.slice().sort((a, b) => {
          let as = a[this.sortKey]
          let bs = b[this.sortKey]
          // make this sort stable
          if (as == bs) return JSON.stringify(a).localeCompare(JSON.stringify(b))
          let res = as > bs ? 1 : as < bs ? -1 : 0
          if (this.sortDirection) res = -res
          return res
        })
        return data
      }
      return this.tableData
    },
    columns() {
      return Object.keys(this.tableData[0]).map(key => {
        const opts = this.opts.columns[key] || this.opts.columns['*'] || {}
        return {
          key: key,
          type: opts.type || typeof this.tableData[0][key],
          options: opts
        }
      })
    },
    rows() {
      return this.sortedData.slice()
      // with pagination: data.slice(page * paged, page * paged + paged);
    }
  },
  methods: {
    iso3ToName,
    th({ options, key }) {
      const title = options.title || key
      const formatter = options.titleFormatter || (v => v)
      return formatter(title)
    },
    cell(value, index, row, formatter = v => v) {
      const displayValue = formatter(value, index, row)
      return displayValue
    },
    url(value) {
      return `/${this.region}/${this.dimension}/${iso3ToSlug(value)}`
    },

    /**
     * Anchors in the first and last table colums triggers
     * tableScroll() when they move in and out of the viewport
     */
    tableScroll(isVisible, entry) {
      if (entry.target.id === 'firstColumn') this.firstColumnIsVisible = isVisible
      if (entry.target.id === 'lastColumn') this.lastColumnIsVisible = isVisible
    },

    /**
     * Re-sorts table rows on column header click
     */
    sortTable(key) {
      if (!this.opts.sortable) return
      if (this.sortKey == key) {
        this.sortDirection = !this.sortDirection
      }
      this.sortKey = key
    }
  },
  mounted() {
    this.sortKey = this.options.initialSortKey
  }
}
</script>

<style>
.table thead th.column-type-number,
.table tbody td.cell-type-number,
.table tbody td.cell-rank {
  text-align: right;
  white-space: nowrap;
}
.table tbody td.cell-type-number,
.table tbody td.cell-rank {
  font-family: menlo, consolas, monaco, monospace;
  font-size: 90%;
}

.arrow {
  width: 0;
  height: 0;
  border: 7px solid transparent;
}
.arrow--right {
  border-left-color: currentColor;
}
.arrow--left {
  border-right-color: currentColor;
}
</style>
